<template>
  <div class="auth-card modal-content">
    <!-- ═══ SIGN IN: single credentials step ═══ -->
    <AuthLoginForm
      v-if="authStore.mode === 'signin'"
      :mobile="authStore.loginMobile"
      :password="authStore.loginPassword"
      :status="authStore.status"
      :error-message="currentErrorMessage"
      :signup-success-message="authStore.signupSuccess"
      @submit="handleLogin"
      @update:mobile="updateLoginMobile"
      @update:password="updateLoginPassword"
      @switch-mode="handleModeToggle"
      @forgot-password="handleForgotPassword"
    />

    <!-- ═══ SIGN UP: details step ═══ -->
    <AuthSignupDetailsForm
      v-else-if="authStore.mode === 'signup' && authStore.step === 'details'"
      :signup-draft="authStore.signupDraft"
      :field-errors="signupFieldErrors"
      :status="authStore.status"
      :error-message="signupErrorMessage"
      @update:signup-draft="updateSignupDraft"
      @submit="handleSignupSendOtp"
      @switch-mode="handleModeToggle"
    />

    <!-- ═══ SIGN UP: OTP verification step ═══ -->
    <AuthOtpStep
      v-else-if="authStore.mode === 'signup' && authStore.step === 'signup-otp'"
      mode="signup"
      :phone-mask="signupPhoneMask"
      :otp="authStore.signupOtp.otp"
      :attempts-left="authStore.signupOtp.attemptsLeft"
      :otp-countdown-seconds="authStore.signupOtpCountdownSeconds"
      :resend-countdown-seconds="authStore.signupResendCountdownSeconds"
      :can-resend="authStore.canResendSignupOtp"
      :status="authStore.status"
      :error-message="currentErrorMessage"
      :mock-otp-hint="authStore.signupOtp.mockOtpHint"
      @update:otp="updateSignupOtp"
      @submit="handleSignupVerifyOtp"
      @resend="handleSignupResendOtp"
      @back="handleSignupOtpBack"
    />

    <!-- ═══ FORGOT PASSWORD: OTP-based reset ═══ -->
    <AuthForgotPasswordForm
      v-else-if="authStore.mode === 'forgot-password'"
      :forgot-draft="authStore.forgotDraft"
      :status="authStore.status"
      :error-message="currentErrorMessage"
      :can-resend="authStore.canResendForgotOtp"
      :otp-countdown-seconds="authStore.forgotOtpCountdownSeconds"
      :resend-countdown-seconds="authStore.forgotResendCountdownSeconds"
      @send-otp="handleForgotSendOtp"
      @resend-otp="handleForgotResendOtp"
      @reset-password="handleResetPassword"
      @update:forgot-draft="updateForgotDraft"
      @back="handleBackToSignin"
    />
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, watch } from 'vue'
import AuthLoginForm from '@/components/auth/AuthLoginForm.vue'
import AuthSignupDetailsForm from '@/components/auth/AuthSignupDetailsForm.vue'
import AuthOtpStep from '@/components/auth/AuthOtpStep.vue'
import AuthForgotPasswordForm from '@/components/auth/AuthForgotPasswordForm.vue'
import { useAuthStore } from '@/stores/auth'

const props = defineProps({
  initialMode: {
    type: String,
    default: 'signin'
  }
})

const emit = defineEmits(['success'])

const authStore = useAuthStore()

let clockIntervalId = null

const normalizeMode = (value) => {
  const normalized = String(value ?? '').trim().toLowerCase()
  if (normalized === 'signup') return 'signup'
  if (normalized === 'forgot-password') return 'forgot-password'
  return 'signin'
}

// ─── Error messages ──────────────────────────────────────────────

const currentErrorMessage = computed(() => {
  const code = authStore.error?.code
  if (!code) return ''

  const messages = {
    INVALID_OTP: 'The OTP entered is incorrect. Please try again.',
    OTP_EXPIRED: 'OTP has expired. Please resend and try again.',
    MAX_ATTEMPTS: 'Maximum OTP attempts reached. Resend OTP to continue.',
    ACCOUNT_NOT_FOUND: 'Account not found for this phone number.',
    INVALID_CREDENTIALS: 'Invalid mobile number or password.',
    ACCOUNT_EXISTS: 'Account already exists. Please sign in.',
    NETWORK_ERROR: 'Unable to reach auth service. Please try again.'
  }

  return messages[code] || authStore.error?.message || 'Authentication failed. Please try again.'
})

const signupFieldErrors = computed(() => {
  if (authStore.error?.code === 'VALIDATION_ERROR') {
    return authStore.error?.meta?.fields || {}
  }
  return {}
})

const signupErrorMessage = computed(() => {
  if (authStore.error?.code !== 'VALIDATION_ERROR') {
    return currentErrorMessage.value
  }
  if (Object.keys(signupFieldErrors.value).length > 0) {
    return ''
  }
  return authStore.error?.message || ''
})

// ─── Mode switching ──────────────────────────────────────────────

const switchMode = (mode) => {
  authStore.setMode(normalizeMode(mode))
}

const handleModeToggle = () => {
  const nextMode = authStore.mode === 'signin' ? 'signup' : 'signin'
  switchMode(nextMode)
}

const handleForgotPassword = () => {
  switchMode('forgot-password')
}

const handleBackToSignin = () => {
  switchMode('signin')
}

// ─── Login handlers ──────────────────────────────────────────────

const updateLoginMobile = (value) => {
  authStore.loginMobile = String(value ?? '')
  if (authStore.error) authStore.error = null
}

const updateLoginPassword = (value) => {
  authStore.loginPassword = String(value ?? '')
  if (authStore.error) authStore.error = null
}

const handleLogin = async () => {
  const session = await authStore.login()
  if (session) {
    emit('success')
  }
}

// ─── Signup handlers ─────────────────────────────────────────────

const updateSignupDraft = (draft) => {
  authStore.signupDraft = { ...authStore.signupDraft, ...draft }
  if (authStore.error?.code === 'VALIDATION_ERROR') {
    authStore.error = null
  }
}

const signupPhoneMask = computed(() => {
  const digits = String(authStore.signupDraft.mobile ?? '').replace(/\D/g, '')
  if (digits.length >= 4) {
    return `+91 ${digits.slice(0, 2)}****${digits.slice(-2)}`
  }
  return '+91 ****'
})

const handleSignupSendOtp = async () => {
  const result = await authStore.signupValidateAndSendOtp(authStore.signupDraft)
  // If result is truthy, the store moved step to 'signup-otp' automatically
}

const updateSignupOtp = (otp) => {
  authStore.signupOtp.otp = String(otp ?? '')
  if (authStore.error) authStore.error = null
}

const handleSignupVerifyOtp = async () => {
  const session = await authStore.signupVerifyOtpAndCreate()
  if (session) {
    emit('success')
  }
}

const handleSignupResendOtp = async () => {
  await authStore.signupResendOtp()
}

const handleSignupOtpBack = () => {
  authStore.step = 'details'
  authStore.status = 'idle'
  authStore.error = null
  authStore.signupOtp.otp = ''
}

// ─── Forgot password handlers ────────────────────────────────────

const updateForgotDraft = (draft) => {
  authStore.forgotDraft = { ...authStore.forgotDraft, ...draft }
  if (authStore.error) authStore.error = null
}

const handleForgotSendOtp = async () => {
  await authStore.sendForgotOtp(authStore.forgotDraft.mobile)
}

const handleForgotResendOtp = async () => {
  await authStore.resendForgotOtp()
}

const handleResetPassword = async () => {
  const result = await authStore.resetPassword()
  if (result) {
    // Password reset => user is now on signin screen
  }
}

// ─── Lifecycle ───────────────────────────────────────────────────

watch(
  () => props.initialMode,
  (mode) => {
    switchMode(mode)
  },
  { immediate: true }
)

onMounted(() => {
  authStore.hydrateSession()
  clockIntervalId = window.setInterval(() => {
    authStore.refreshClock()
  }, 1000)
})

onBeforeUnmount(() => {
  if (clockIntervalId) {
    window.clearInterval(clockIntervalId)
    clockIntervalId = null
  }
})
</script>

<style scoped>
.auth-card {
  border: none;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  padding: 36px 32px;
}

/* Disable the theme hover animation that turns buttons black */
.auth-card :deep(.primary-btn1)::after {
  display: none !important;
}
.auth-card :deep(.primary-btn1:hover) {
  opacity: 0.88;
}
.auth-card :deep(.primary-btn1:hover > span) {
  transform: none !important;
  opacity: 1 !important;
}

/* Shared floating label input styling */
.auth-card :deep(.form-inner) {
  position: relative;
}

.auth-card :deep(.form-inner label) {
  font-size: 13px;
  font-weight: 500;
  color: #555;
  margin-bottom: 6px;
}

.auth-card :deep(.form-inner input) {
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  min-height: 48px;
  padding: 12px 14px;
  font-size: 15px;
  transition: border-color 0.2s;
  width: 100%;
}

.auth-card :deep(.form-inner input:focus) {
  border-color: var(--primary-color1, #23b5ee);
  box-shadow: 0 0 0 3px rgba(35, 181, 238, 0.1);
  outline: none;
}

/* Auth heading styles */
.auth-card :deep(.auth-heading) {
  text-align: center;
  margin-bottom: 28px;
}

.auth-card :deep(.auth-brand-icon) {
  width: 48px;
  height: 48px;
  margin: 0 auto 12px;
  display: block;
  object-fit: contain;
}

.auth-card :deep(.auth-title) {
  font-size: 24px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 4px;
}

.auth-card :deep(.auth-subtitle) {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
}

/* Primary button styling */
.auth-card :deep(.auth-submit-btn) {
  width: 100%;
  height: 48px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
}

/* Divider with text */
.auth-card :deep(.auth-divider) {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #9ca3af;
  font-size: 13px;
  margin: 20px 0;
}

.auth-card :deep(.auth-divider)::before,
.auth-card :deep(.auth-divider)::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #e5e7eb;
}

/* Switch mode link */
.auth-card :deep(.auth-switch) {
  text-align: center;
  font-size: 14px;
  color: #6b7280;
  margin-top: 20px;
}

.auth-card :deep(.auth-switch a),
.auth-card :deep(.auth-switch button) {
  color: var(--primary-color1, #23b5ee);
  font-weight: 600;
  text-decoration: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
}

.auth-card :deep(.auth-switch a:hover),
.auth-card :deep(.auth-switch button:hover) {
  text-decoration: underline;
}

/* Error alert */
.auth-card :deep(.auth-error) {
  background: #fef2f2;
  border: 1px solid #fee2e2;
  border-radius: 8px;
  padding: 10px 14px;
  color: #dc2626;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}

@media (max-width: 767px) {
  .auth-card {
    padding: 24px 20px;
    border-radius: 0;
    box-shadow: none;
    min-height: 100dvh;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
}
</style>
